import * as THREE from 'three';

const bodyMaterial = new THREE.MeshStandardMaterial({ color: '#d99a5b', roughness: 0.8 });
const headMaterial = new THREE.MeshStandardMaterial({ color: '#f0c9a0', roughness: 0.7 });

const IDLE_BOB_SPEED = 1.6;
const IDLE_BOB_AMOUNT = 0.03;
const REACT_DURATION = 0.6;

function createVillagerMesh(): THREE.Group {
  const group = new THREE.Group();

  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.16, 0.34, 4, 8), bodyMaterial);
  body.position.y = 0.35;
  body.castShadow = true;
  group.add(body);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.14, 12, 10), headMaterial);
  head.position.y = 0.66;
  head.castShadow = true;
  group.add(head);

  return group;
}

/** A single low-poly villager with an idle bob plus a one-shot correct/incorrect reaction. */
export class VillagerActor {
  readonly group: THREE.Group;
  private readonly baseY: number;
  private phase = Math.random() * Math.PI * 2;
  private reactElapsed = REACT_DURATION;
  private reactSign = 1;

  constructor(position: THREE.Vector3) {
    this.group = createVillagerMesh();
    this.group.position.copy(position);
    this.baseY = position.y;
  }

  reactCorrect(): void {
    this.reactElapsed = 0;
    this.reactSign = 1;
  }

  reactIncorrect(): void {
    this.reactElapsed = 0;
    this.reactSign = -1;
  }

  update(delta: number): void {
    this.phase += delta * IDLE_BOB_SPEED;
    let y = this.baseY + Math.sin(this.phase) * IDLE_BOB_AMOUNT;
    let scaleY = 1;
    let rotationY = 0;

    if (this.reactElapsed < REACT_DURATION) {
      this.reactElapsed += delta;
      const t = Math.min(this.reactElapsed / REACT_DURATION, 1);
      if (this.reactSign > 0) {
        // quick hop + a little spin
        const hop = Math.sin(t * Math.PI) * 0.28;
        y += hop;
        rotationY = Math.sin(t * Math.PI * 2) * 0.4 * (1 - t);
      } else {
        // duck down and shrink slightly, then recover
        const duck = Math.sin(t * Math.PI) * 0.16;
        y -= duck;
        scaleY = 1 - Math.sin(t * Math.PI) * 0.22;
      }
    }

    this.group.position.y = y;
    this.group.scale.y = scaleY;
    this.group.rotation.y = rotationY;
  }

  dispose(): void {
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) child.geometry.dispose();
    });
  }
}

export function disposeVillagerMaterials(): void {
  bodyMaterial.dispose();
  headMaterial.dispose();
}
